import React from 'react'
import { Box, IconButton, Stack, Typography } from '@mui/material'
import { Delete, DragIndicator } from '@mui/icons-material'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { com } from '@eidu/web'
import FieldData from '../../domain/entity/FieldData'
import FieldInput from '../../components/input/FieldInput'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import FieldType = com.eidu.sharedlib.entity.field.FieldType
import TypeValidationError = com.eidu.sharedlib.entity.validation.TypeValidationError

type SortableFieldRowProps = {
  id: string
  index: number
  fieldData: FieldData
  availableEntityTypes: readonly EntityType[]
  submitting: boolean
  errors?: readonly TypeValidationError[]
  onRemove: () => void
  onFieldTypeChange: (typeName: string) => Promise<void>
  onRequiredChange: () => Promise<void>
  onReferenceTypesChange: (types: readonly EntityTypeId[]) => Promise<void>
  onFieldNameChange: (name: string) => Promise<void>
}

const SortableFieldRow = ({
  id,
  index,
  fieldData,
  availableEntityTypes,
  submitting,
  errors,
  onRemove,
  onFieldTypeChange,
  onRequiredChange,
  onReferenceTypesChange,
  onFieldNameChange,
}: SortableFieldRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  }

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        padding: 2,
        backgroundColor: 'background.paper',
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Stack direction="row" alignItems="center" spacing={1} flexGrow={1}>
          <IconButton
            size="small"
            disabled={submitting}
            aria-label="Reorder field"
            sx={{ cursor: submitting ? 'not-allowed' : 'grab', touchAction: 'none' }}
            {...attributes}
            {...listeners}
          >
            <DragIndicator fontSize="small" />
          </IconButton>
          <Typography variant="subtitle1">{fieldData.field.name || `Field ${index + 1}`}</Typography>
        </Stack>
        <IconButton color="error" onClick={onRemove} size="small" disabled={submitting}>
          <Delete fontSize="small" />
        </IconButton>
      </Stack>
      <Box mt={2}>
        <FieldInput
          availableEntityTypes={availableEntityTypes}
          disabled={submitting}
          editable={!fieldData.existing}
          fieldType={fieldData.field.type}
          handleFieldTypeChange={(event) => onFieldTypeChange(event.target.value)}
          handleRequiredChange={onRequiredChange}
          handleReferenceTypesChange={onReferenceTypesChange}
          handleFieldNameChange={onFieldNameChange}
          idPrefix={`manage-entity-type-field-${index}`}
          name={fieldData.field.name}
          referenceTypes={
            fieldData.field.validReferenceTypes?.asJsReadonlySetView()?.let((set) => Array.from(set)) || []
          }
          referenceTypesError={
            fieldData.field.type === FieldType.Reference
              ? !fieldData.field.validReferenceTypes?.asJsReadonlySetView()?.let((set) => set.size > 0)
              : false
          }
          required={fieldData.field.required}
          updating={false}
          errors={errors}
          editableName
        />
      </Box>
    </Box>
  )
}

export default SortableFieldRow
